import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { CMS_POST_API_CONFIG } from '../config';
import { ApiResponse, PaginatedResponse } from '../models';
import {
  ImageGallery,
  GalleryRoute,
  CreateImageGalleryDto,
  UpdateImageGalleryDto,
  AddGalleryItemDto,
  ReorderGalleryItemsDto,
  SetGalleryCoverDto,
  ImageGalleryListParams,
} from '../models/image-gallery.model';

@Injectable({ providedIn: 'root' })
export class ImageGalleryService {
  private readonly _http = inject(HttpClient);
  private readonly _apiBase: string;

  private readonly _readerBase: string;

  constructor() {
    const config = inject(CMS_POST_API_CONFIG);
    this._apiBase = `${config.apiBaseUrl}/api/image-galleries`;
    this._readerBase = `${config.apiBaseUrl}/api/reader/image-galleries`;
  }

  // ─── Public (unauthenticated) ──────────────────────────────────────────────

  getPublicGalleries(
    params: Omit<ImageGalleryListParams, 'publishStatus'>
  ): Observable<PaginatedResponse<ImageGallery>> {
    const httpParams = new HttpParams({ fromObject: this._toStringParams(params as Record<string, unknown>) });
    return this._http.get<PaginatedResponse<ImageGallery>>(`${this._apiBase}/public`, { params: httpParams });
  }

  getPublicGalleryBySlug(slug: string, orgId: string): Observable<ApiResponse<ImageGallery>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<ImageGallery>>(`${this._apiBase}/public/${slug}`, { params: httpParams });
  }

  getPublicGalleryRoutes(orgId: string): Observable<ApiResponse<GalleryRoute[]>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<GalleryRoute[]>>(`${this._apiBase}/public/routes`, { params: httpParams });
  }

  // ─── Authenticated ─────────────────────────────────────────────────────────

  listGalleries(params: ImageGalleryListParams): Observable<PaginatedResponse<ImageGallery>> {
    const httpParams = new HttpParams({ fromObject: this._toStringParams(params as Record<string, unknown>) });
    return this._http.get<PaginatedResponse<ImageGallery>>(this._apiBase, { params: httpParams });
  }

  getGalleryById(id: string, orgId: string): Observable<ApiResponse<ImageGallery>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<ImageGallery>>(`${this._apiBase}/${id}`, { params: httpParams });
  }

  createGallery(dto: CreateImageGalleryDto): Observable<ApiResponse<ImageGallery>> {
    return this._http.post<ApiResponse<ImageGallery>>(this._apiBase, dto);
  }

  updateGallery(id: string, dto: UpdateImageGalleryDto): Observable<ApiResponse<ImageGallery>> {
    return this._http.patch<ApiResponse<ImageGallery>>(`${this._apiBase}/${id}`, dto);
  }

  publishGallery(
    id: string,
    publishStatus: 'draft' | 'internal' | 'public' = 'public'
  ): Observable<ApiResponse<ImageGallery>> {
    return this._http.patch<ApiResponse<ImageGallery>>(`${this._apiBase}/${id}/publish`, { publishStatus });
  }

  deleteGallery(id: string, orgId: string): Observable<{ success: boolean; message: string }> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.delete<{ success: boolean; message: string }>(
      `${this._apiBase}/${id}`,
      { params: httpParams }
    );
  }

  // ─── Items ────────────────────────────────────────────────────────────────

  addItem(galleryId: string, dto: AddGalleryItemDto): Observable<ApiResponse<ImageGallery>> {
    return this._http.post<ApiResponse<ImageGallery>>(`${this._apiBase}/${galleryId}/items`, dto);
  }

  removeItem(galleryId: string, itemId: string, orgId: string): Observable<ApiResponse<ImageGallery>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this._http.delete<ApiResponse<ImageGallery>>(
      `${this._apiBase}/${galleryId}/items/${itemId}`,
      { params: httpParams }
    );
  }

  reorderItems(galleryId: string, dto: ReorderGalleryItemsDto): Observable<ApiResponse<ImageGallery>> {
    return this._http.patch<ApiResponse<ImageGallery>>(
      `${this._apiBase}/${galleryId}/items/reorder`,
      dto
    );
  }

  setCover(galleryId: string, dto: SetGalleryCoverDto): Observable<ApiResponse<ImageGallery>> {
    return this._http.patch<ApiResponse<ImageGallery>>(
      `${this._apiBase}/${galleryId}/cover`,
      dto
    );
  }
  // ─── Reader (authenticated, visibility-filtered) ───────────────────────────

  getReaderGalleries(orgId: string, page?: number, limit?: number): Observable<PaginatedResponse<ImageGallery>> {
    let params = new HttpParams().set('orgId', orgId);
    if (page !== undefined) params = params.set('page', String(page));
    if (limit !== undefined) params = params.set('limit', String(limit));
    return this._http.get<PaginatedResponse<ImageGallery>>(this._readerBase, { params });
  }

  getReaderGalleryBySlug(slug: string, orgId: string): Observable<ApiResponse<ImageGallery>> {
    const params = new HttpParams().set('orgId', orgId);
    return this._http.get<ApiResponse<ImageGallery>>(`${this._readerBase}/${slug}`, { params });
  }
  // ─── Helpers ──────────────────────────────────────────────────────────────

  private _toStringParams(obj: Record<string, unknown>): Record<string, string> {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [k, String(v)])
    );
  }
}
